import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axios';
import { getRole } from '../../utils/role';

const initialState = {
    devices: [],
    status: 'idle',
    currentPage: 1,
    totalPages: 1,
    total: 0,
    error: null,
    deviceHistory: [],
    historyStatus: 'idle',
    historyError: null,
    // Ping count state
    pingCount: null,
    pingStatus: 'idle',
    pingError: null,
    // Device status control feature flag
    deviceStatusControlEnabled: false,
};

// FETCH DEVICES
export const fetchDevices = createAsyncThunk(
    'devices/fetchDevices',
    async ({ page = 1, limit = 100, device_id }, { rejectWithValue }) => {
        try {
            const role = getRole();
            let url = '';
            const deviceIdParam =
                device_id && String(device_id).trim().length > 0
                    ? `&device_id=${encodeURIComponent(String(device_id).trim())}`
                    : '';

            if (role === 'SUPPORT') {
                url = `/super-admin/device-assignments?page=${page}&limit=${limit}${deviceIdParam}`;
            } else if (role === 'PARTNER' || role === 'TEAM_MEMBER') {
                url = `/admin/device-assignments?page=${page}&limit=${limit}${deviceIdParam}`;
            } else if (role === 'FIELD_OFFICER') {
                url = `/field-officer/devices?page=${page}&limit=${limit}${deviceIdParam}`;
            } else {
                return rejectWithValue('Invalid role');
            }

            const { data } = await axiosInstance.get(url);
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || err.response?.data?.msg || 'Failed to fetch devices');
        }
    }
);

// ADD DEVICE
export const addDevice = createAsyncThunk(
    'devices/addDevice',
    async (deviceData, { rejectWithValue }) => {
        try {
            const role = getRole();
            const url = role === 'SUPPORT'
                ? '/super-admin/device-assignments'
                : '/admin/device-assignments';

            const { data } = await axiosInstance.post(url, {
                data: deviceData,
            });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || err.response?.data?.msg || 'Failed to add device');
        }
    }
);

// MAP DEVICE / UPDATE DEVICE ASSIGNMENT
export const mapDevice = createAsyncThunk(
    'devices/mapDevice',
    async ({ device_id, ...fields }, { rejectWithValue }) => {
        try {
            const role = getRole();
            const url = role === 'SUPPORT'
                ? `/super-admin/device-assignments/${device_id}`
                : `/admin/device-assignments/${device_id}`;

            const { data } = await axiosInstance.put(url, { data: fields });
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || err.response?.data?.msg || 'Failed to map device');
        }
    }
);

// FETCH DEVICE HISTORY
export const fetchDeviceHistory = createAsyncThunk(
    'devices/fetchDeviceHistory',
    async (device_id, { rejectWithValue }) => {
        try {
            const role = getRole();
            const url = role === 'SUPPORT'
                ? `/super-admin/device-assignments/${device_id}/history`
                : `/admin/device-assignments/${device_id}/history`;

            const { data } = await axiosInstance.get(url);
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || err.response?.data?.msg || 'Failed to fetch device history');
        }
    }
);

// FETCH DEVICE PING COUNT (SUPPORT role only)
export const fetchDevicePingCount = createAsyncThunk(
    'devices/fetchDevicePingCount',
    async ({ device_id, date }, { rejectWithValue }) => {
        try {
            const role = getRole();
            if (role !== 'SUPPORT') {
                return rejectWithValue('Ping count is only available for SUPPORT role');
            }

            const url = `/super-admin/device/${device_id}/ping-count?date=${date}`;
            const { data } = await axiosInstance.get(url);
            return data; // { success, device_id, date, pingCount }
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.error ||
                err.response?.data?.msg ||
                'Failed to fetch device ping count'
            );
        }
    }
);

// FETCH DEVICE STATUS CONTROL FEATURE FLAG
export const fetchDeviceStatusControlFlag = createAsyncThunk(
    'devices/fetchDeviceStatusControlFlag',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.get('/feature-flags');
            const flags = data?.data || [];
            const flag = flags.find(f => f.flag_name === 'device_status_control');
            return flag?.flag_value ?? false;
        } catch (err) {
            return false;
        }
    }
);

// TOGGLE DEVICE STATUS (SUPPORT only)
export const toggleDeviceStatus = createAsyncThunk(
    'devices/toggleDeviceStatus',
    async (device_id, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.patch(
                `/super-admin/device-assignments/${device_id}/toggle-status`
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Failed to toggle device status');
        }
    }
);

export const toggleDeviceUpdateStatus = createAsyncThunk(
    'devices/toggleDeviceUpdateStatus',
    async (device_id, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.patch(
                `/super-admin/device-assignments/${device_id}/toggle-update-status`
            );
            return data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Failed to toggle device update status');
        }
    }
);

const devicesSlice = createSlice({
    name: 'devices',
    initialState,
    reducers: {
        setCurrentPage(state, action) {
            state.currentPage = action.payload;
        },
        resetDevicesState(state) {
            state.devices = [];
            state.status = 'idle';
            state.error = null;
        },
    },
    extraReducers(builder) {
        builder
            // Fetch devices
            .addCase(fetchDevices.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchDevices.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.devices = action.payload.devices || [];
                state.total = action.payload.total || 0;
                state.totalPages = action.payload.totalPages || 1;
                state.currentPage = action.payload.page || 1;
            })
            .addCase(fetchDevices.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Add device
            .addCase(addDevice.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(addDevice.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Add the new device to the list
                if (action.payload.device) {
                    state.devices.unshift(action.payload.device);
                }
            })
            .addCase(addDevice.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Map device
            .addCase(mapDevice.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(mapDevice.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const index = state.devices.findIndex(
                    (d) => d.device_id === action.payload.device.device_id
                );
                if (index !== -1) {
                    state.devices[index] = action.payload.device;
                }
            })
            .addCase(mapDevice.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Fetch device history
            .addCase(fetchDeviceHistory.pending, (state) => {
                state.historyStatus = 'loading';
                state.historyError = null;
            })
            .addCase(fetchDeviceHistory.fulfilled, (state, action) => {
                state.historyStatus = 'succeeded';
                state.deviceHistory = action.payload.history || [];
            })
            .addCase(fetchDeviceHistory.rejected, (state, action) => {
                state.historyStatus = 'failed';
                state.historyError = action.payload;
            })
            // Fetch device ping count
            .addCase(fetchDevicePingCount.pending, (state) => {
                state.pingStatus = 'loading';
                state.pingError = null;
                state.pingCount = null;
            })
            .addCase(fetchDevicePingCount.fulfilled, (state, action) => {
                state.pingStatus = 'succeeded';
                state.pingCount = action.payload?.pingCount ?? 0;
            })
            .addCase(fetchDevicePingCount.rejected, (state, action) => {
                state.pingStatus = 'failed';
                state.pingError = action.payload;
                state.pingCount = null;
            })
            // Feature flag for device status control
            .addCase(fetchDeviceStatusControlFlag.fulfilled, (state, action) => {
                state.deviceStatusControlEnabled = action.payload;
            })
            // Toggle device status
            .addCase(toggleDeviceStatus.fulfilled, (state, action) => {
                const { device_id, device_status } = action.payload;
                const index = state.devices.findIndex((d) => d.device_id === device_id);
                if (index !== -1) {
                    state.devices[index].device_status = device_status;
                }
            })
            .addCase(toggleDeviceStatus.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(toggleDeviceUpdateStatus.fulfilled, (state, action) => {
                const { device_id, auto_update_enabled } = action.payload;
                const index = state.devices.findIndex((d) => d.device_id === device_id);
                if (index !== -1) {
                    state.devices[index].auto_update_enabled = auto_update_enabled;
                }
            })
            .addCase(toggleDeviceUpdateStatus.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export const { setCurrentPage, resetDevicesState } = devicesSlice.actions;
export default devicesSlice.reducer;
